import {
  HHAlert,
  HHButton,
  HHDialog,
  HHDialogActions,
  HHDialogContent,
  HHDialogTitle,
  HHTypography,
} from '@hinge-health/react-component-library';
import { CONFIGURATION_STATUS_TRANSITION_LABELS } from '../constants/partner-data-gateway.constant';
import { ConfigurationStatus } from '../domain/enums';

export const CONFIGURATION_STATUS_DIALOG_TEST_ID =
  'pdg-configuration-status-confirmation-dialog';
export const CONFIGURATION_STATUS_DIALOG_CONFIRM_TEST_ID =
  'pdg-configuration-status-confirmation-dialog-confirm-button';
export const CONFIGURATION_STATUS_DIALOG_CANCEL_TEST_ID =
  'pdg-configuration-status-confirmation-dialog-cancel-button';
export const CONFIGURATION_STATUS_DIALOG_ERROR_TEST_ID =
  'pdg-configuration-status-confirmation-dialog-error-alert';

/*
 * MUI's Modal does not infer `aria-labelledby` / `aria-describedby` from
 * HHDialogTitle / HHDialogContent, so the dialog would otherwise open with
 * no accessible name at all. These ids wire the title and the body — plus
 * the error alert when it is showing — into the dialog itself.
 */
const DIALOG_TITLE_ID = 'pdg-configuration-status-confirmation-dialog-title';
const DIALOG_MESSAGE_ID =
  'pdg-configuration-status-confirmation-dialog-message';
const DIALOG_ERROR_ID = 'pdg-configuration-status-confirmation-dialog-error';

interface TransitionCopy {
  title: string;
  message: string;
}

const TRANSITION_COPY: Record<ConfigurationStatus, TransitionCopy> = {
  [ConfigurationStatus.Published]: {
    title: 'Publish this configuration?',
    message:
      'Published configurations are evaluated against every incoming file for this provider.',
  },
  [ConfigurationStatus.Deleted]: {
    title: 'Delete this configuration?',
    message:
      'Deleting stops this configuration from matching any further files. It cannot be undone.',
  },
  [ConfigurationStatus.Draft]: {
    title: 'Move this configuration to draft?',
    message: 'Draft configurations are not evaluated against incoming files.',
  },
};

export interface ConfigurationStatusConfirmationDialogProps {
  open: boolean;
  targetStatus: ConfigurationStatus;
  configurationName: string;
  loading?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation gate in front of every configuration status change. Follows
 * the same shape as the ingestion control dialog so both destructive
 * actions in the module read the same way.
 */
export const ConfigurationStatusConfirmationDialog = ({
  open,
  targetStatus,
  configurationName,
  loading = false,
  error = null,
  onConfirm,
  onCancel,
}: ConfigurationStatusConfirmationDialogProps): JSX.Element => {
  const { title, message } = TRANSITION_COPY[targetStatus];
  const isDelete = targetStatus === ConfigurationStatus.Deleted;

  const describedBy = [DIALOG_MESSAGE_ID, error ? DIALOG_ERROR_ID : null]
    .filter(Boolean)
    .join(' ');

  return (
    <HHDialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
      /*
       * Delete is the module's one irreversible action, so it follows the
       * ARIA alertdialog pattern rather than the plain dialog pattern.
       * The role goes through `PaperProps` because that is the node MUI
       * gives `role="dialog"` and the aria-* ids to — a bare `role` prop
       * lands on the outer Modal instead, leaving two dialog roles on the
       * page.
       */
      PaperProps={isDelete ? { role: 'alertdialog' } : undefined}
      aria-labelledby={DIALOG_TITLE_ID}
      aria-describedby={describedBy}
      data-testid={CONFIGURATION_STATUS_DIALOG_TEST_ID}
    >
      <HHDialogTitle id={DIALOG_TITLE_ID}>{title}</HHDialogTitle>
      <HHDialogContent>
        {/*
          `variant-bypass` rather than `hhVariant="body1"`: HHTypography only
          forwards arbitrary props (here, `id`) to MUI on the bypass variant —
          every named variant drops them, which would leave the dialog's
          `aria-describedby` pointing at an element that does not exist.
        */}
        <HHTypography
          hhVariant="variant-bypass"
          variant="body1"
          id={DIALOG_MESSAGE_ID}
          sx={{ marginBottom: 2 }}
        >
          {message}
        </HHTypography>
        <HHTypography
          hhVariant="variant-bypass"
          variant="body2"
          color="textSecondary"
        >
          Configuration: <strong>{configurationName}</strong>
        </HHTypography>
        {error && (
          <HHAlert
            hhVariant="variant-bypass"
            variant="outlined"
            severity="error"
            id={DIALOG_ERROR_ID}
            data-testid={CONFIGURATION_STATUS_DIALOG_ERROR_TEST_ID}
            sx={{ marginTop: 2 }}
          >
            {error}
          </HHAlert>
        )}
      </HHDialogContent>
      <HHDialogActions>
        <HHButton
          hhVariant="variant-bypass"
          variant="outlined"
          disabled={loading}
          onClick={onCancel}
          data-testid={CONFIGURATION_STATUS_DIALOG_CANCEL_TEST_ID}
        >
          Cancel
        </HHButton>
        <HHButton
          hhVariant="variant-bypass"
          variant="contained"
          color={isDelete ? 'error' : 'primary'}
          disabled={loading}
          onClick={onConfirm}
          data-testid={CONFIGURATION_STATUS_DIALOG_CONFIRM_TEST_ID}
        >
          {loading
            ? 'Working…'
            : CONFIGURATION_STATUS_TRANSITION_LABELS[targetStatus]}
        </HHButton>
      </HHDialogActions>
    </HHDialog>
  );
};
